import { useQueryClient } from '@tanstack/react-query';

import React, { createContext, useContext, useMemo } from 'react';

import { EntityMetadataModel, ParameterMetadata, ParameterType } from '../../../api/api';
import { ExpressionMetadataProvider, useExpressionMetadata } from '../../../components/ExpressionMetadataContext';
import useFlowQueries from '../../FlowDesigner/Hooks/useFlowQueries';
import { ViewRefModel } from '../../FlowDesigner/Models/ViewRefModel';
import { getEntityMetadataModelFromJsonSchema } from '../../FlowDesigner/Services/EntityMetadataModelConverter';
import ExpressionVariableScopeCalculatorService, { ExpectedObjectTypeResolver } from '../../FlowDesigner/Services/ExpressionVariableScopeCalculatorService';
import { Bundle } from '../Models/Bundle';
import { ParameterTypes } from '@@common';

interface IDesignerAppContext {
	bundle: Bundle | null;
	ViewById: Record<string, Bundle['Views'][number]>;
	SharedComponentById: Record<string, Bundle['SharedComponents'][number]>;
	EmbeddedResourceById: Record<string, Bundle['EmbeddedResources'][number]>;
}

const emptyDesignerAppContext: IDesignerAppContext = {
	bundle: null,
	ViewById: {},
	SharedComponentById: {},
	EmbeddedResourceById: {},
};

export const DesignerAppContext = createContext<IDesignerAppContext>(emptyDesignerAppContext);

export function useDesignerApp() {
	return useContext(DesignerAppContext);
}

type DesignerAppProviderProps = {
	bundle: Bundle | null;
	children: React.ReactNode;
};

export default function DesignerAppProvider({ bundle, children }: DesignerAppProviderProps) {
	const value = useMemo<IDesignerAppContext>(
		() => ({
			bundle,
			ViewById: Object.fromEntries((bundle?.Views ?? []).map((view) => [view.Id, view])),
			SharedComponentById: Object.fromEntries((bundle?.SharedComponents ?? []).map((sharedComponent) => [sharedComponent.Id, sharedComponent])),
			EmbeddedResourceById: Object.fromEntries((bundle?.EmbeddedResources ?? []).map((resource) => [resource.Id, resource])),
		}),
		[bundle],
	);
	const { expressions } = useExpressionMetadata();
	const flowQueries = useFlowQueries();
	const queryClient = useQueryClient();

	const expectedObjectTypeResolver = useMemo<ExpectedObjectTypeResolver | undefined>(() => {
		if (!bundle) return undefined;

		return (
			_parameterMetadata: ParameterMetadata,
			referencedParam: ParameterMetadata,
			model: Record<string, any>,
			parsed: {
				paramName: string;
				useRequestSchema?: boolean;
				useResponseSchema?: boolean;
				useInputSchema?: boolean;
				useOutputSchema?: boolean;
			},
		): EntityMetadataModel | undefined => {
			// Handle SharedComponentRef
			if (referencedParam.type === ParameterType.SharedComponentRef) {
				const refValue = model[referencedParam.name] as { Id?: string } | undefined;
				if (refValue?.Id) {
					const sc = value.SharedComponentById[refValue.Id];
					if (sc?.InputSchemaValue?.Value) {
						return getEntityMetadataModelFromJsonSchema(sc.InputSchemaValue.Value);
					}
				}
				return undefined;
			}

			// Handle View-specific typeRef suffixes
			if ((parsed.useInputSchema || parsed.useOutputSchema) && referencedParam.typeCustom === ParameterTypes.ViewId) {
				const viewRef = model[parsed.paramName] as ViewRefModel | undefined;
				if (viewRef?.Id) {
					const view = value.ViewById[viewRef.Id];
					if (view) {
						if (parsed.useInputSchema && view.InputSchemaValue?.Value) {
							return getEntityMetadataModelFromJsonSchema(view.InputSchemaValue.Value);
						} else if (parsed.useOutputSchema && view.OutputSchemaValue?.Value) {
							return getEntityMetadataModelFromJsonSchema(view.OutputSchemaValue.Value);
						}
					}
				}
				return undefined;
			}

			return undefined;
		};
	}, [bundle, value.SharedComponentById, value.ViewById]);

	const bundleAwareScopeCalculator = useMemo(
		() => new ExpressionVariableScopeCalculatorService(expressions || [], flowQueries, queryClient, expectedObjectTypeResolver),
		[expressions, flowQueries, queryClient, expectedObjectTypeResolver],
	);

	return (
		<DesignerAppContext.Provider value={value}>
			<ExpressionMetadataProvider expressions={expressions} expressionScopeCalculator={bundleAwareScopeCalculator}>
				{children}
			</ExpressionMetadataProvider>
		</DesignerAppContext.Provider>
	);
}
